import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Box, Typography, MenuItem, Avatar, ListItemIcon, ListItemText, Menu, IconButton, Tooltip } from '@mui/material'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import { styled } from '@mui/material/styles'
import { createGlobalStyle } from 'styled-components'
import {
  AGORA_RTC_LAYOUTS,
  ChamberScreenAgoraVideoSurface,
  RTC_PROVIDERS,
  buildChamberShellSnapshot,
  createChamberScreenLocalParticipantIdentity,
  readLocalParticipantIdentity,
  useRtcSessionShell,
} from 'src/live'
import ChamberVideoPlaceholder from './ChamberVideoPlaceholder'

// Common media URL prefix for profile pictures
const mediaUrlPrefix = 'https://media.aumhum.com/images/prod/'

const VideoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  minWidth: '100%',
  minHeight: '100%',
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(circle at top, rgba(196, 181, 253, 0.6) 0%, rgba(255, 255, 255, 0.98) 36%, rgba(243, 232, 255, 0.96) 100%)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  isolation: 'isolate',
}))

const HeroVideoWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'transparent',
}))

// Kiosk shell styles; chamber layout presentation is owned by the shared shell.
const ModernKioskVideoCardStyles = createGlobalStyle`
  .chamber-video-container .focus-selection-button,
  .chamber-video-container .shared-video-controls,
  .chamber-video-container .agora-rtc-localBtnContainer {
    transition: opacity 0.3s ease, visibility 0.3s ease !important;
  }

  .chamber-video-container.controls-hidden .focus-selection-button,
  .chamber-video-container.controls-hidden .shared-video-controls,
  .chamber-video-container.controls-hidden .agora-rtc-localBtnContainer {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }

  .chamber-video-container.modern-kiosk-stage .agora-rtc-localBtnContainer {
    position: fixed !important;
    left: 50% !important;
    right: auto !important;
    bottom: 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px !important;
    width: fit-content !important;
    max-width: calc(100% - 32px) !important;
    margin: 0 !important;
    transform: translateX(-50%) !important;
    -webkit-transform: translateX(-50%) !important;
    z-index: 10000 !important;
  }

  .chamber-video-container.modern-kiosk-stage .agora-rtc-localBtnContainer button {
    pointer-events: auto !important;
    transform: none !important;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.25) !important;
  }

  .chamber-video-container.modern-kiosk-stage .agora-rtc-localBtnContainer button[data-custom-state='enabled']:not([data-role='agora-end-call']) {
    background: rgba(255, 255, 255, 0.96) !important;
    border: 1px solid rgba(148, 163, 184, 0.38) !important;
    color: #0f172a !important;
  }

  .chamber-video-container.modern-kiosk-stage .agora-rtc-localBtnContainer button[data-custom-state='disabled']:not([data-role='agora-end-call']) {
    background: rgba(15, 23, 42, 0.78) !important;
    border: 1px solid rgba(255, 255, 255, 0.18) !important;
    color: #f8fafc !important;
  }

  .chamber-video-container.modern-kiosk-stage .agora-rtc-localBtnContainer button[data-role='agora-end-call'] {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 1) 100%) !important;
    border: 1px solid rgba(255, 255, 255, 0.28) !important;
    color: #ffffff !important;
    box-shadow: 0 12px 28px rgba(220, 38, 38, 0.35) !important;
  }

  @media (max-width: 960px), (orientation: portrait) {
    .chamber-video-container.modern-kiosk-stage .agora-rtc-localBtnContainer {
      bottom: 20px !important;
      max-width: calc(100% - 24px) !important;
    }
  }
`

export default function ModernChamberVideoInterface({
  agoraRtcProps,
  agoraRtmProps,
  agoraCallbacks,
  serverGivenNextAction,
  localParticipantIdentity = null,
  chamberName,
  clinicBrandId,
  flowType,
  sessionRoutingDecision,
}) {
  const containerRef = useRef(null)
  const enableLegacyDomEnhancements = false
  const [uidToUsernameMap, setUidToUsernameMap] = useState({})
  const uidToUsernameMapRef = useRef({})
  const [remoteUsers, setRemoteUsers] = useState([])
  const remoteUsersRef = useRef([])
  const [focusMenuAnchor, setFocusMenuAnchor] = useState(null)
  const [focusedUid, setFocusedUid] = useState(null)
  const chamberProviderInitialConfig = useMemo(
    () => ({
      rtcProps: agoraRtcProps || {},
      rtmProps: agoraRtmProps || null,
    }),
    [agoraRtcProps, agoraRtmProps],
  )
  const chamberShell = useRtcSessionShell({
    providerId: RTC_PROVIDERS.agora,
    sessionKey: `${agoraRtcProps?.channel || ''}:${agoraRtcProps?.uid || ''}`,
    surfacePolicyId: 'kiosk-screen',
    localParticipantIdentity:
      readLocalParticipantIdentity(
        localParticipantIdentity,
        createChamberScreenLocalParticipantIdentity(
          serverGivenNextAction?.chamberScreenAction || serverGivenNextAction,
        ),
      ) || null,
    providerInitialConfig: chamberProviderInitialConfig,
  })
  const chamberShellSnapshot = useMemo(
    () =>
      buildChamberShellSnapshot({
        controllerState: chamberShell,
      }),
    [chamberShell.dominantParticipantId, chamberShell.participants, chamberShell.visibleParticipants],
  )
  const localRtcUid =
    agoraRtcProps?.uid !== undefined && agoraRtcProps?.uid !== null
      ? String(agoraRtcProps.uid)
      : null
  
  // Auto-hide controls state
  const [showControls, setShowControls] = useState(true)
  const hideControlsTimeoutRef = useRef(null)
  const lastActivityTimeRef = useRef(Date.now())
  
  // Keep refs in sync with state
  useEffect(() => {
    uidToUsernameMapRef.current = uidToUsernameMap
    remoteUsersRef.current = remoteUsers
  }, [uidToUsernameMap, remoteUsers])

  const resolveRawUsernameForUid = useCallback(
    (uid, fallbackUsername = '') => {
      if (uid === null || uid === undefined) {
        return fallbackUsername || ''
      }

      const directCandidates = [uid, String(uid)]
      const numericUid = Number(uid)
      if (!Number.isNaN(numericUid)) {
        directCandidates.push(numericUid)
      }

      for (const candidate of directCandidates) {
        if (uidToUsernameMap[candidate]) {
          return uidToUsernameMap[candidate]
        }
      }

      const matchingKey = Object.keys(uidToUsernameMap).find((key) => {
        if (key === String(uid)) {
          return true
        }

        const keyNumber = Number(key)
        return !Number.isNaN(keyNumber) && !Number.isNaN(numericUid) && keyNumber === numericUid
      })

      if (matchingKey) {
        return uidToUsernameMap[matchingKey]
      }

      return fallbackUsername || ''
    },
    [uidToUsernameMap],
  )

  const isActualRemoteParticipant = useCallback(
    (uid) => {
      if (uid === null || uid === undefined || uid === '') {
        return false
      }

      const normalizedUid = String(uid)
      if (normalizedUid === '0') {
        return false
      }

      return localRtcUid ? normalizedUid !== localRtcUid : true
    },
    [localRtcUid],
  )

  const upsertRemoteRtcUser = useCallback((user, overrides = {}) => {
    if (!user || user.uid === undefined || user.uid === null) {
      return
    }

    setRemoteUsers((previousUsers) => {
      const existingIndex = previousUsers.findIndex(
        (existingUser) => String(existingUser.uid) === String(user.uid),
      )

      const nextUser = {
        ...(existingIndex >= 0 ? previousUsers[existingIndex] : {}),
        ...user,
        ...overrides,
      }

      if (existingIndex === -1) {
        return [...previousUsers, nextUser]
      }

      const currentUser = previousUsers[existingIndex]
      const hasChanged = Object.keys(nextUser).some((key) => currentUser[key] !== nextUser[key])

      if (!hasChanged) {
        return previousUsers
      }

      const nextUsers = [...previousUsers]
      nextUsers[existingIndex] = nextUser
      return nextUsers
    })
  }, [])

  const removeRemoteRtcUser = useCallback((uid) => {
    setRemoteUsers((previousUsers) => {
      const nextUsers = previousUsers.filter(
        (existingUser) => String(existingUser.uid) !== String(uid),
      )
      return nextUsers.length === previousUsers.length ? previousUsers : nextUsers
    })
  }, [])
  
  // Force center the button container using JavaScript
  useEffect(() => {
    if (!enableLegacyDomEnhancements) {
      return undefined
    }

    const centerButtonContainer = () => {
      console.log('[Button Container] Searching for Agora control strip...')
      
      if (containerRef.current) {
        // Find ALL divs
        const allDivs = containerRef.current.querySelectorAll('div')
        console.log(`[Button Container] Checking ${allDivs.length} div elements...`)
        
        let agoraStripFound = false
        
        // LOG ALL DIVS to see what's actually there
        console.log('[Button Container] === DUMPING ALL DIV STYLES ===')
        allDivs.forEach((div, idx) => {
          const computedStyle = window.getComputedStyle(div)
          const bottom = computedStyle.bottom
          const zIndex = computedStyle.zIndex
          
          // Log any div that might be relevant (has position absolute or non-auto bottom)
          if (computedStyle.position === 'absolute' || (bottom !== 'auto' && bottom !== '0px')) {
            console.log(`Div ${idx}:`, {
              position: computedStyle.position,
              bottom: bottom,
              left: computedStyle.left,
              zIndex: zIndex,
              display: computedStyle.display,
              width: computedStyle.width,
            })
          }
        })
        console.log('[Button Container] === END DUMP ===')
        
        allDivs.forEach((div, idx) => {
          const computedStyle = window.getComputedStyle(div)
          
          const position = computedStyle.position
          const zIndex = computedStyle.zIndex
          
          // Agora overrides our position:absolute to position:relative!
          // Look for relative OR absolute position with zIndex 10000
          const isAgoraContainer = 
            (position === 'relative' || position === 'absolute') && 
            (zIndex === '10000' || parseInt(zIndex) === 10000)
          
          if (isAgoraContainer) {
            console.log(`[Button Container] 🎯 FOUND AGORA STRIP! Div ${idx}:`, {
              position: computedStyle.position,
              bottom: computedStyle.bottom,
              left: computedStyle.left,
              width: computedStyle.width,
              zIndex: computedStyle.zIndex,
              display: computedStyle.display,
            })
            
            // Force center styling with maximum specificity
            div.style.setProperty('position', 'absolute', 'important')
            div.style.setProperty('left', '50%', 'important')
            div.style.setProperty('right', 'auto', 'important')
            div.style.setProperty('transform', 'translateX(-50%)', 'important')
            div.style.setProperty('-webkit-transform', 'translateX(-50%)', 'important')
            div.style.setProperty('width', 'fit-content', 'important')
            div.style.setProperty('max-width', 'fit-content', 'important')
            div.style.setProperty('min-width', 'auto', 'important')
            div.style.setProperty('margin-left', '0', 'important')
            div.style.setProperty('margin-right', '0', 'important')
            
            console.log('[Button Container] ✅ Centering applied! Changed position to absolute and centered.')
            
            agoraStripFound = true
          }
        })
        
        if (!agoraStripFound) {
          console.log('[Button Container] ⚠️ No suitable candidates found!')
        }
      }
    }
    
    // Apply multiple times with different delays
    console.log('[Button Container] Setting up centering for Agora strip...')
    centerButtonContainer()
    
    const timer1 = setTimeout(centerButtonContainer, 500)
    const timer2 = setTimeout(centerButtonContainer, 1000)
    const timer3 = setTimeout(centerButtonContainer, 2000)
    const timer4 = setTimeout(centerButtonContainer, 3000)
    const timer5 = setTimeout(centerButtonContainer, 5000)
    
    // Set up a continuous interval to keep re-applying (in case Agora overwrites)
    const interval = setInterval(centerButtonContainer, 2000)
    
    // Set up a mutation observer to re-apply when DOM changes
    const observer = new MutationObserver(() => {
      centerButtonContainer()
    })
    
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }
    
    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
      clearTimeout(timer4)
      clearTimeout(timer5)
      clearInterval(interval)
      observer.disconnect()
    }
  }, [])
  
  // Debug: Log map state changes for remote users
  useEffect(() => {
    const mapKeys = Object.keys(uidToUsernameMap)
    const localUidNum = agoraRtcProps?.uid !== undefined ? parseInt(agoraRtcProps.uid) : null
    
    const remoteEntries = mapKeys
      .filter(key => {
        const uidNum = parseInt(key)
        return !isNaN(uidNum) && (localUidNum === null || uidNum !== localUidNum)
      })
      .map(key => {
        const value = uidToUsernameMap[key]
        return {
          key,
          keyType: typeof key,
          value: value ? (value.length < 100 ? value : value.substring(0, 100) + '...') : 'null',
          valueType: typeof value,
          isJson: value && (value.includes('{') || value.includes('"name"')),
        }
      })
    
    console.log('🔵 [REMOTE USER FLOW] uidToUsernameMap state changed:', {
      totalKeys: mapKeys.length,
      allKeys: mapKeys,
      localUid: localUidNum,
      remoteEntries,
      remoteEntryCount: remoteEntries.length,
      fullMap: Object.entries(uidToUsernameMap).reduce((acc, [k, v]) => {
        acc[k] = typeof v === 'string' && v.length < 100 ? v : (typeof v === 'string' ? v.substring(0, 100) + '...' : '...')
        return acc
      }, {}),
    })
  }, [uidToUsernameMap, agoraRtcProps?.uid])
  
  // Auto-hide controls after 10 seconds of inactivity
  useEffect(() => {
    const handleUserActivity = () => {
      const now = Date.now()
      const timeSinceLastActivity = now - lastActivityTimeRef.current
      
      // Throttle: Only process if at least 1000ms has passed since last activity
      if (timeSinceLastActivity < 1000) {
        return
      }
      
      lastActivityTimeRef.current = now
      setShowControls(true)
      
      // Clear any existing timer
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current)
        hideControlsTimeoutRef.current = null
      }
      
      // Set new hide timer
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false)
        hideControlsTimeoutRef.current = null
      }, 10000) // Hide after 10 seconds of inactivity
    }
    
    // Start the initial timer
    hideControlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false)
      hideControlsTimeoutRef.current = null
    }, 10000)
    
    // Add event listeners for mouse move and touch
    window.addEventListener('mousemove', handleUserActivity)
    window.addEventListener('touchstart', handleUserActivity)
    window.addEventListener('touchmove', handleUserActivity)
    
    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleUserActivity)
      window.removeEventListener('touchstart', handleUserActivity)
      window.removeEventListener('touchmove', handleUserActivity)
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current)
        hideControlsTimeoutRef.current = null
      }
    }
  }, [])
  
  // Helper to parse RTM username
  const parseRtmUsername = (username) => {
    if (!username || typeof username !== 'string' || username.trim() === '') {
      return { role: null, name: 'Unknown', profilePicId: null, identityKey: null }
    }
    try {
      const parsed = JSON.parse(username)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { role: null, name: username, profilePicId: null, identityKey: null }
      }
      return {
        role: parsed.role || null,
        name: parsed.name || 'Unknown',
        profilePicId: parsed.profilePicId || null,
        identityKey: parsed.identityKey || null,
      }
    } catch (error) {
      return { role: null, name: username, profilePicId: null, identityKey: null }
    }
  }
  
  // Helper to get profile picture URL
  const getProfilePictureUrl = (profilePicId) => {
    if (!profilePicId) return null
    if (profilePicId.startsWith('http://') || profilePicId.startsWith('https://')) {
      return profilePicId
    }
    // Use common prefix with single slash
    return `${mediaUrlPrefix}${profilePicId}`
  }
  
  // Helper to get initials
  const getInitials = (name) => {
    if (!name || name === 'Me') return 'Me'
    
    // Don't show initials for loading/unknown states
    if (name === 'Loading...' || name === 'Guest' || name === 'Unknown') {
      return 'U'
    }
    
    const trimmedName = name.trim()
    const words = trimmedName.split(/\s+/).filter(w => w.length > 0)
    
    if (words.length > 1) {
      // Multiple words: use first letter of first and last word
      return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    } else if (trimmedName.length > 0) {
      // Single word: use first letter
      return trimmedName.charAt(0).toUpperCase()
    }
    
    return 'U'
  }

  const isGenericParticipantName = useCallback((value) => {
    return (
      !value ||
      value === 'Unknown' ||
      value === 'Loading...' ||
      value === 'Guest' ||
      /^Participant \d+$/i.test(value) ||
      /^User \d+$/i.test(value)
    )
  }, [])

  const mentorUid = useMemo(
    () =>
      remoteUsers.find((user) => {
        if (!isActualRemoteParticipant(user?.uid)) {
          return false
        }

        const parsed = parseRtmUsername(resolveRawUsernameForUid(user.uid))
        return parsed?.role === 'mentor'
      })?.uid ?? null,
    [isActualRemoteParticipant, remoteUsers, resolveRawUsernameForUid],
  )

  const focusMenuParticipants = chamberShellSnapshot.focusMenuParticipants

  const focusMenuOpen = Boolean(focusMenuAnchor)
  const activeSpotlightUid = chamberShellSnapshot.activeSpotlightUid

  useEffect(() => {
    if (!chamberShell.isManualFocusActive) {
      if (focusedUid !== null) {
        setFocusedUid(null)
      }
      return
    }

    if (
      activeSpotlightUid !== null &&
      activeSpotlightUid !== undefined &&
      String(focusedUid) !== String(activeSpotlightUid)
    ) {
      setFocusedUid(activeSpotlightUid)
    }
  }, [activeSpotlightUid, chamberShell.isManualFocusActive, focusedUid])

  // Add local user's RTM username to the map
  useEffect(() => {
    if (agoraRtcProps?.uid !== undefined && agoraRtmProps?.username) {
      const uid = agoraRtcProps.uid
      const uidNum = parseInt(uid)
      const uidStr = uid.toString()
      
      console.log('Adding local user to map:', {
        uid,
        uidNum,
        uidStr,
        username: agoraRtmProps.username,
      })
      
      setUidToUsernameMap((prev) => ({
        ...prev,
        [uid]: agoraRtmProps.username,
        [uidNum]: agoraRtmProps.username,
        [uidStr]: agoraRtmProps.username,
      }))
    }
  }, [agoraRtcProps?.uid, agoraRtmProps?.username])

  useEffect(() => {
    if (!chamberShell.isManualFocusActive || focusedUid === null || focusedUid === undefined) {
      return
    }

    const validFocusUids = new Set(
      remoteUsers
        .filter((user) => isActualRemoteParticipant(user?.uid))
        .map((user) => String(user.uid)),
    )

    if (localRtcUid) {
      validFocusUids.add(String(localRtcUid))
    }

    if (!validFocusUids.has(String(focusedUid))) {
      setFocusedUid(null)
    }
  }, [chamberShell.isManualFocusActive, focusedUid, isActualRemoteParticipant, localRtcUid, remoteUsers])

  // Enhanced callbacks to track users and usernames
  const enhancedCallbacks = {
    ...agoraCallbacks,
    'participants-updated': (snapshot) => {
      const dedupedRemoteParticipants = Array.isArray(snapshot?.remoteParticipants)
        ? Array.from(
            snapshot.remoteParticipants.reduce((participantsByUid, participant) => {
              if (!isActualRemoteParticipant(participant?.uid)) {
                return participantsByUid
              }

              const participantUid = String(participant.uid)
              const participantIdentityKey =
                typeof participant?.identityKey === 'string' && participant.identityKey.trim()
                  ? participant.identityKey.trim()
                  : participantUid
              const nextParticipant = {
                uid: participantUid,
                rawUsername: participant?.rawUsername || '',
                displayName: participant?.displayName || '',
                role: participant?.role || 'participant',
                profilePicId: participant?.profilePicId || null,
                identityKey: participant?.identityKey || null,
                hasAudio: Boolean(participant?.hasAudio),
                hasVideo: Boolean(participant?.hasVideo),
              }
              const existingParticipant = participantsByUid.get(participantIdentityKey)

              if (!existingParticipant) {
                participantsByUid.set(participantIdentityKey, nextParticipant)
                return participantsByUid
              }

              const shouldPreferNextParticipant =
                (nextParticipant.hasVideo && !existingParticipant.hasVideo) ||
                (!isGenericParticipantName(nextParticipant.displayName) &&
                  isGenericParticipantName(existingParticipant.displayName))

              participantsByUid.set(participantIdentityKey, {
                ...existingParticipant,
                ...nextParticipant,
                uid: shouldPreferNextParticipant ? nextParticipant.uid : existingParticipant.uid,
                rawUsername: nextParticipant.rawUsername || existingParticipant.rawUsername,
                displayName: nextParticipant.displayName || existingParticipant.displayName,
                role:
                  nextParticipant.role === 'mentor' || existingParticipant.role !== 'mentor'
                    ? nextParticipant.role
                    : existingParticipant.role,
                profilePicId: nextParticipant.profilePicId || existingParticipant.profilePicId,
                identityKey: nextParticipant.identityKey || existingParticipant.identityKey,
                hasAudio: existingParticipant.hasAudio || nextParticipant.hasAudio,
                hasVideo: existingParticipant.hasVideo || nextParticipant.hasVideo,
              })

              return participantsByUid
            }, new Map()).values(),
          ).sort((left, right) => Number(left.uid) - Number(right.uid))
        : []
      const hasNamedRemoteParticipant = dedupedRemoteParticipants.some((participant) => {
        const parsedUsername = parseRtmUsername(participant?.rawUsername || '')
        const resolvedName =
          parsedUsername?.name && !isGenericParticipantName(parsedUsername.name)
            ? parsedUsername.name
            : participant?.displayName || ''

        return !isGenericParticipantName(resolvedName)
      })
      const nextRemoteParticipants = dedupedRemoteParticipants.filter((participant) => {
        if (!hasNamedRemoteParticipant) {
          return true
        }

        const parsedUsername = parseRtmUsername(participant?.rawUsername || '')
        const resolvedName =
          parsedUsername?.name && !isGenericParticipantName(parsedUsername.name)
            ? parsedUsername.name
            : participant?.displayName || ''

        return !isGenericParticipantName(resolvedName)
      })
      const nextUsernameMap = {
        ...(snapshot?.usernameMap && typeof snapshot.usernameMap === 'object'
          ? snapshot.usernameMap
          : {}),
      }

      delete nextUsernameMap[0]
      delete nextUsernameMap['0']

      if (localRtcUid && agoraRtmProps?.username) {
        const numericLocalUid = Number(localRtcUid)
        nextUsernameMap[localRtcUid] = agoraRtmProps.username
        if (!Number.isNaN(numericLocalUid)) {
          nextUsernameMap[numericLocalUid] = agoraRtmProps.username
        }
      }

      setRemoteUsers((previousUsers) => {
        const previousSignature = previousUsers
          .map((participant) =>
            [
              participant?.uid,
              participant?.identityKey,
              participant?.rawUsername,
              participant?.displayName,
              participant?.role,
              participant?.hasAudio ? '1' : '0',
              participant?.hasVideo ? '1' : '0',
            ].join(':'),
          )
          .sort()
          .join('|')
        const nextSignature = nextRemoteParticipants
          .map((participant) =>
            [
              participant?.uid,
              participant?.identityKey,
              participant?.rawUsername,
              participant?.displayName,
              participant?.role,
              participant?.hasAudio ? '1' : '0',
              participant?.hasVideo ? '1' : '0',
            ].join(':'),
          )
          .sort()
          .join('|')

        return previousSignature === nextSignature ? previousUsers : nextRemoteParticipants
      })

      setUidToUsernameMap((previousMap) => {
        const previousEntries = Object.entries(previousMap).sort(([leftKey], [rightKey]) =>
          String(leftKey).localeCompare(String(rightKey)),
        )
        const nextEntries = Object.entries(nextUsernameMap).sort(([leftKey], [rightKey]) =>
          String(leftKey).localeCompare(String(rightKey)),
        )

        if (
          previousEntries.length === nextEntries.length &&
          previousEntries.every(
            ([previousKey, previousValue], index) =>
              previousKey === nextEntries[index]?.[0] &&
              previousValue === nextEntries[index]?.[1],
          )
        ) {
          return previousMap
        }

        return nextUsernameMap
      })

      agoraCallbacks?.['participants-updated']?.(snapshot)
    },
    'user-joined': (user) => agoraCallbacks?.['user-joined']?.(user),
    'user-published': (user, mediaType) => agoraCallbacks?.['user-published']?.(user, mediaType),
    'user-unpublished': (user, mediaType) =>
      agoraCallbacks?.['user-unpublished']?.(user, mediaType),
    'user-left': (user) => agoraCallbacks?.['user-left']?.(user),
    'rtm-user-joined': (uid, username) => agoraCallbacks?.['rtm-user-joined']?.(uid, username),
    'rtm-user-left': (uid) => agoraCallbacks?.['rtm-user-left']?.(uid),
  }

  // Function to style status indicators like mentor portal
  const styleStatusIndicators = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    // Find all video cards - both participant and local user
    const allVideoCards = container.querySelectorAll('[data-uid], [data-role]')
    
    allVideoCards.forEach((videoCard) => {
      const uid = videoCard.getAttribute('data-uid')
      const role = videoCard.getAttribute('data-role')
      
      // Check if this is a small card (participant or local user, not mentor)
      const isSmallCard = role === 'participant' || (uid && (uid === '0' || parseInt(uid) === agoraRtcProps?.uid))
      const isMentorCard = role === 'mentor'
      
      if (!isSmallCard && !isMentorCard) return
      
      // Find status indicator containers - look for divs with SVG icons
      const statusContainers = videoCard.querySelectorAll('div[style*="position: absolute"]')
      
      statusContainers.forEach((container) => {
        const hasSvg = container.querySelector('svg[viewBox*="24"]')
        if (!hasSvg) return
        
        // Check if this is a status indicator (has mic/camera icons)
        const svgs = container.querySelectorAll('svg')
        const isStatusIndicator = Array.from(svgs).some(svg => {
          const viewBox = svg.getAttribute('viewBox') || ''
          return viewBox.includes('24')
        })
        
        if (!isStatusIndicator) return
        
        // Apply mentor portal styling
        container.style.background = 'rgba(0, 0, 0, 0.3)'
        container.style.backdropFilter = 'blur(8px) saturate(150%)'
        container.style.webkitBackdropFilter = 'blur(8px) saturate(150%)'
        container.style.borderRadius = '8px'
        container.style.padding = '4px 6px'
        container.style.border = '1px solid rgba(255, 255, 255, 0.2)'
        container.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'
        container.style.display = 'flex'
        container.style.gap = '4px'
        container.style.alignItems = 'center'
        
        // Position based on card type
        if (isSmallCard) {
          // Bottom left for small cards
          container.style.bottom = '8px'
          container.style.left = '8px'
          container.style.top = 'auto'
          container.style.right = 'auto'
          container.style.transform = 'none'
        } else if (isMentorCard) {
          // Top center for mentor cards
          container.style.left = '50%'
          container.style.transform = 'translateX(-50%)'
          container.style.right = 'auto'
          container.style.top = '8px'
          container.style.bottom = 'auto'
        }
        
        // Style individual SVG icons
        const icons = container.querySelectorAll('svg')
        icons.forEach((icon) => {
          icon.style.width = '16px'
          icon.style.height = '16px'
          icon.style.color = '#ffffff'
          icon.style.filter = 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
        })
      })
    })
  }, [agoraRtcProps?.uid])

  // Function to parse and update usernames from JSON - can be called independently
  const parseAndUpdateUsernames = useCallback(() => {
    if (!enableLegacyDomEnhancements) return

    const container = containerRef.current
    if (!container) return

    // Get local user's username from RTM props (for chamber room, this is the JSON)
    const localUserUsername = agoraRtmProps?.username
    const localUserUid = agoraRtcProps?.uid

    // FIRST: Simple and aggressive - find ALL text nodes containing JSON and replace them
    // This catches cases where Agora UIKit renders JSON directly in text nodes
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    )
    
    let textNode
    while ((textNode = walker.nextNode())) {
      const text = textNode.textContent || ''
      
      // Check if this text node contains JSON
      if (text.includes('{') && text.includes('"name"') && text.length < 500) {
        // Check if this text node is within a video container
        let parent = textNode.parentElement
        let videoParent = null
        let uid = null
        
        while (parent && parent !== container) {
          const dataUid = parent.getAttribute('data-uid')
          if (dataUid) {
            videoParent = parent
            uid = dataUid
            break
          }
          // Also check if parent has video element (might be local user)
          if (parent.querySelector('video') || parent.closest('[data-uid]')) {
            const closestUid = parent.closest('[data-uid]')?.getAttribute('data-uid')
            if (closestUid) {
              videoParent = parent.closest('[data-uid]')
              uid = closestUid
              break
            }
            // For local user, might not have data-uid yet
            if (localUserUid !== undefined && localUserUsername) {
              videoParent = parent
              uid = localUserUid.toString()
              break
            }
          }
          parent = parent.parentElement
        }
        
        // If we found a video parent, try to parse and replace
        if (videoParent || uid) {
          try {
            const parsed = JSON.parse(text.trim())
            if (parsed && parsed.name && typeof parsed.name === 'string' && parsed.name !== 'Unknown') {
              // Replace the text node content
              textNode.textContent = parsed.name
              
              // Update the map
              if (uid) {
                const uidNum = parseInt(uid)
                if (!isNaN(uidNum)) {
                  setUidToUsernameMap((prev) => {
                    const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                    const isJson = text.includes('{') || text.includes('"name"')
                    const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    const shouldUpdate = !currentValue || (isJson && !currentIsJson) || (isJson && currentValue !== text.trim())
                    
                    if (shouldUpdate) {
                      return {
                        ...prev,
                        [uid]: text.trim(),
                        [uidNum]: text.trim(),
                        [uid.toString()]: text.trim(),
                      }
                    }
                    return prev
                  })
                }
              }
            }
          } catch (e) {
            // Try regex extraction as fallback
            const nameMatch = text.match(/"name"\s*:\s*"([^"]+)"/)
            if (nameMatch && nameMatch[1] && nameMatch[1] !== 'Unknown') {
              textNode.textContent = nameMatch[1]
              
              // Update the map
              if (uid) {
                const uidNum = parseInt(uid)
                if (!isNaN(uidNum)) {
                  setUidToUsernameMap((prev) => {
                    const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                    const isJson = text.includes('{') || text.includes('"name"')
                    const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    const shouldUpdate = !currentValue || (isJson && !currentIsJson) || (isJson && currentValue !== text.trim())
                    
                    if (shouldUpdate) {
                      return {
                        ...prev,
                        [uid]: text.trim(),
                        [uidNum]: text.trim(),
                        [uid.toString()]: text.trim(),
                      }
                    }
                    return prev
                  })
                }
              }
            }
          }
        }
      }
    }

    // SECOND: Do a comprehensive search for ALL elements that might contain JSON
    // This catches elements even if they don't match our specific selectors
    const allElements = container.querySelectorAll('*')
    
    allElements.forEach((el) => {
      const text = el.textContent || el.innerText || ''
      
      // Check if text looks like JSON (contains { and "name")
      if (text.includes('{') && text.includes('"name"') && text.length < 500) {
        // Skip if element is hidden
        const style = window.getComputedStyle(el)
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
          return
        }
        
        // Skip if element is inside a placeholder component
        const isInPlaceholder = el.closest('[class*="placeholder"], [class*="Placeholder"], [class*="CustomVideo"]')
        if (isInPlaceholder) {
          return
        }
        
        // Check if element is within a video container
        // For local user, the video might not have data-uid, so check if it's the local video
        let videoParent = el.closest('[data-uid]')
        let isLocalUserVideo = false
        
        // If no data-uid found, check if this might be the local user's video
        if (!videoParent && localUserUid !== undefined && localUserUsername) {
          // Check if element is in a container that might be the local video
          const possibleLocalContainer = el.closest('[class*="local"], [class*="Local"], [class*="video"]')
          if (possibleLocalContainer) {
            // Check if this container has a video element and no data-uid
            const hasVideo = possibleLocalContainer.querySelector('video')
            const hasDataUid = possibleLocalContainer.hasAttribute('data-uid') || possibleLocalContainer.closest('[data-uid]')
            if (hasVideo && !hasDataUid) {
              isLocalUserVideo = true
              videoParent = possibleLocalContainer
            }
          }
        }
        
        // Must be in a video container (with data-uid or identified as local)
        if (!videoParent && !isLocalUserVideo) {
          return
        }
        
        // Verify the video parent is actually visible
        if (videoParent) {
          const videoRect = videoParent.getBoundingClientRect()
          if (videoRect.width === 0 || videoRect.height === 0) {
            return
          }
        }
        
        // Be more aggressive - if it's in a video container and contains JSON, it's likely a username pill
        // Don't filter based on styling - Agora UIKit might render it in various ways
        const hasUsernameClass = el.classList.toString().toLowerCase().includes('username')
        const isTextElement = el.tagName === 'SPAN' || el.tagName === 'P' || el.tagName === 'DIV' || el.tagName === 'H1' || el.tagName === 'H2' || el.tagName === 'H3'
        
        // Only skip if it's clearly not a text element and doesn't have username class
        // This is more permissive to catch all username pills
        if (!hasUsernameClass && !isTextElement && el.tagName !== 'BUTTON' && el.tagName !== 'A') {
          // Still allow it if it has visible text content (might be a custom element)
          if (!text.trim() || text.trim().length < 3) {
            return
          }
        }
        
        // Determine which username to use
        // For local user video, use the RTM props username if available
        let usernameToParse = text
        if (isLocalUserVideo && localUserUsername) {
          // If text matches the local username or contains the same JSON structure, use local username
          if (text === localUserUsername || (text.includes('{') && localUserUsername.includes('{'))) {
            usernameToParse = localUserUsername
          }
        }
        
        try {
          const parsed = JSON.parse(usernameToParse)
          if (parsed && parsed.name && typeof parsed.name === 'string') {
            const role = parsed.role
            const shouldHide = role === 'auditor' || role === 'panelist'
            
            // Get UID from video parent
            let uid = null
            if (videoParent) {
              uid = videoParent.getAttribute('data-uid')
            } else if (isLocalUserVideo && localUserUid !== undefined) {
              uid = localUserUid.toString()
            }
            
            if (shouldHide) {
              el.textContent = ''
              el.innerText = ''
              el.style.display = 'none'
            } else {
              // Show name for any role (including chamber-room)
              // ALWAYS replace JSON with parsed name, regardless of whether it's in the map
              // This ensures videos always show names, not JSON
              if (text.includes('{') || text.includes('"name"') || isLocalUserVideo) {
                el.textContent = parsed.name
                el.innerText = parsed.name
                el.style.display = ''
                
                // ALSO update the uidToUsernameMap so focus dropdown can use it
                if (uid) {
                  const uidNum = parseInt(uid)
                  if (!isNaN(uidNum)) {
                    const isRemoteUser = !isLocalUserVideo && agoraRtcProps?.uid !== undefined && uidNum !== parseInt(agoraRtcProps.uid)
                    
                    console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames - Found JSON in DOM for UID:', {
                      uid,
                      uidNum,
                      isRemoteUser,
                      isLocalUserVideo,
                      usernameToParse: usernameToParse.length < 100 ? usernameToParse : usernameToParse.substring(0, 100) + '...',
                      parsedName: parsed.name,
                      role: parsed.role,
                    })
                    
                    setUidToUsernameMap((prev) => {
                      // ALWAYS update if we found JSON, even if a plain string exists
                      // This ensures we have complete RTM data
                      const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                      const isJson = usernameToParse.includes('{') || usernameToParse.includes('"name"')
                      const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                      
                      // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                      const shouldUpdate = !currentValue || 
                        (isJson && !currentIsJson) ||
                        (isJson && currentValue !== usernameToParse)
                      
                      console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames - Map update decision:', {
                        uid,
                        isRemoteUser,
                        currentValue: currentValue ? (currentValue.length < 100 ? currentValue : currentValue.substring(0, 100) + '...') : 'null',
                        currentIsJson,
                        isJson,
                        shouldUpdate,
                        prevMapKeys: Object.keys(prev),
                        prevMapSize: Object.keys(prev).length,
                      })
                      
                      if (shouldUpdate) {
                        const updated = {
                          ...prev,
                          [uid]: usernameToParse, // Store original JSON
                          [uidNum]: usernameToParse,
                          [uid.toString()]: usernameToParse,
                        }
                        console.log('🟢 [REMOTE USER FLOW] ✅ UPDATED uidToUsernameMap from parseAndUpdateUsernames', {
                          uid,
                          isRemoteUser,
                          newKeys: Object.keys(updated),
                          newMapSize: Object.keys(updated).length,
                          storedValue: usernameToParse.length < 100 ? usernameToParse : usernameToParse.substring(0, 100) + '...',
                        })
                        return updated
                      } else {
                        console.log('🟡 [REMOTE USER FLOW] ⏭️ SKIPPED updating uidToUsernameMap from parseAndUpdateUsernames', {
                          uid,
                          isRemoteUser,
                          reason: !currentValue ? 'shouldUpdate is false (no current value)' : 
                                  (isJson && !currentIsJson) ? 'shouldUpdate is false (JSON vs non-JSON)' :
                                  (isJson && currentValue !== usernameToParse) ? 'shouldUpdate is false (different JSON)' : 'unknown',
                        })
                      }
                      return prev
                    })
                  }
                }
              }
            }
          }
        } catch (e) {
          // Try regex extraction as fallback
          const nameMatch = text.match(/"name"\s*:\s*"([^"]+)"/)
          if (nameMatch && nameMatch[1]) {
            if (text.includes('{') || isLocalUserVideo) {
              el.textContent = nameMatch[1]
              el.innerText = nameMatch[1]
              el.style.display = ''
              
              // Update map for regex-extracted names too
              let uid = null
              if (videoParent) {
                uid = videoParent.getAttribute('data-uid')
              } else if (isLocalUserVideo && localUserUid !== undefined) {
                uid = localUserUid.toString()
              }
              
              if (uid) {
                const uidNum = parseInt(uid)
                if (!isNaN(uidNum)) {
                  setUidToUsernameMap((prev) => {
                    // ALWAYS update if we found JSON, even if a plain string exists
                    const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                    const isJson = text.includes('{') || text.includes('"name"')
                    const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    
                    // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                    const shouldUpdate = !currentValue || 
                      (isJson && !currentIsJson) ||
                      (isJson && currentValue !== text)
                    
                    if (shouldUpdate) {
                      console.log('parseAndUpdateUsernames (regex) - Updating map for UID', uid, 'with text:', text, 'previous:', currentValue)
                      return {
                        ...prev,
                        [uid]: text, // Store original text (which contains JSON)
                        [uidNum]: text,
                        [uid.toString()]: text,
                      }
                    }
                    return prev
                  })
                }
              }
            }
          } else if (isLocalUserVideo && localUserUsername) {
            // Last resort: try parsing the local username directly
            try {
              const localParsed = JSON.parse(localUserUsername)
              if (localParsed?.name) {
                el.textContent = localParsed.name
                el.innerText = localParsed.name
                el.style.display = ''
              }
            } catch (e2) {
              // Failed, skip
            }
          }
        }
      }
    })
    
    // Also do targeted search within video containers (more efficient for known structures)
    const videoContainers = container.querySelectorAll('[data-uid]')
    
    videoContainers.forEach((videoContainer) => {
      const uid = videoContainer.getAttribute('data-uid')
      if (!uid) return
      
      // Find username elements within this specific video container
      const usernameSelectors = [
        `[data-uid="${uid}"] [class*="username"]`,
        `[data-uid="${uid}"] span`,
        `[data-uid="${uid}"] p`,
        `[data-uid="${uid}"] div[style*="background"]`,
      ]
      
      usernameSelectors.forEach((selector) => {
        try {
          const usernameElements = container.querySelectorAll(selector)
          
          usernameElements.forEach((el) => {
            const text = el.textContent || el.innerText || ''
            
            // Check if text looks like JSON
            if (text.includes('{') && text.includes('"name"')) {
              try {
                const parsed = JSON.parse(text)
                if (parsed && parsed.name && typeof parsed.name === 'string') {
                  const role = parsed.role
                  const shouldHide = role === 'auditor' || role === 'panelist'
                  
                  if (shouldHide) {
                    el.textContent = ''
                    el.innerText = ''
                    el.style.display = 'none'
                  } else {
                    // Always update if it's JSON
                    if (text.includes('{')) {
                      el.textContent = parsed.name
                      el.innerText = parsed.name
                      el.style.display = ''
                      
                      // ALSO update the uidToUsernameMap
                      const uidNum = parseInt(uid)
                      if (!isNaN(uidNum)) {
                        const isRemoteUser = agoraRtcProps?.uid !== undefined && uidNum !== parseInt(agoraRtcProps.uid)
                        
                        console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames (targeted) - Found JSON in DOM for UID:', {
                          uid,
                          uidNum,
                          isRemoteUser,
                          text: text.length < 100 ? text : text.substring(0, 100) + '...',
                          parsedName: parsed.name,
                          role: parsed.role,
                        })
                        
                        setUidToUsernameMap((prev) => {
                          // ALWAYS update if we found JSON, even if a plain string exists
                          const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                          const isJson = text.includes('{') || text.includes('"name"')
                          const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                          
                          // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                          const shouldUpdate = !currentValue || 
                            (isJson && !currentIsJson) ||
                            (isJson && currentValue !== text)
                          
                          console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames (targeted) - Map update decision:', {
                            uid,
                            isRemoteUser,
                            currentValue: currentValue ? (currentValue.length < 100 ? currentValue : currentValue.substring(0, 100) + '...') : 'null',
                            currentIsJson,
                            isJson,
                            shouldUpdate,
                            prevMapKeys: Object.keys(prev),
                            prevMapSize: Object.keys(prev).length,
                          })
                          
                          if (shouldUpdate) {
                            const updated = {
                              ...prev,
                              [uid]: text, // Store original JSON
                              [uidNum]: text,
                              [uid.toString()]: text,
                            }
                            console.log('🟢 [REMOTE USER FLOW] ✅ UPDATED uidToUsernameMap from parseAndUpdateUsernames (targeted)', {
                              uid,
                              isRemoteUser,
                              newKeys: Object.keys(updated),
                              newMapSize: Object.keys(updated).length,
                              storedValue: text.length < 100 ? text : text.substring(0, 100) + '...',
                            })
                            return updated
                          } else {
                            console.log('🟡 [REMOTE USER FLOW] ⏭️ SKIPPED updating uidToUsernameMap from parseAndUpdateUsernames (targeted)', {
                              uid,
                              isRemoteUser,
                              reason: !currentValue ? 'shouldUpdate is false (no current value)' : 
                                      (isJson && !currentIsJson) ? 'shouldUpdate is false (JSON vs non-JSON)' :
                                      (isJson && currentValue !== text) ? 'shouldUpdate is false (different JSON)' : 'unknown',
                            })
                          }
                          return prev
                        })
                      }
                    }
                  }
                }
              } catch (e) {
                // Try regex extraction
                const nameMatch = text.match(/"name"\s*:\s*"([^"]+)"/)
                if (nameMatch && nameMatch[1]) {
                  if (text.includes('{')) {
                    el.textContent = nameMatch[1]
                    el.innerText = nameMatch[1]
                    el.style.display = ''
                    
                    // Update map for regex-extracted names
                    const uidNum = parseInt(uid)
                    if (!isNaN(uidNum)) {
                      setUidToUsernameMap((prev) => {
                        // ALWAYS update if we found JSON, even if a plain string exists
                        const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                        const isJson = text.includes('{') || text.includes('"name"')
                        const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                        
                        // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                        const shouldUpdate = !currentValue || 
                          (isJson && !currentIsJson) ||
                          (isJson && currentValue !== text)
                        
                        if (shouldUpdate) {
                          console.log('parseAndUpdateUsernames (targeted regex) - Updating map for UID', uid, 'with text:', text, 'previous:', currentValue)
                          return {
                            ...prev,
                            [uid]: text, // Store original text (which contains JSON)
                            [uidNum]: text,
                            [uid.toString()]: text,
                          }
                        }
                        return prev
                      })
                    }
                  }
                }
              }
            }
          })
        } catch (selectorError) {
          // Selector failed, continue
        }
      })
    })
    
    // Also extract already-parsed names (plain strings) from DOM and update map
    // This catches cases where the name is already displayed correctly on video card
    const allVideoContainers = container.querySelectorAll('[data-uid]')
    allVideoContainers.forEach((videoContainer) => {
      const uid = videoContainer.getAttribute('data-uid')
      if (!uid) return
      
      const uidNum = parseInt(uid)
      if (isNaN(uidNum)) return
      
      // Skip if we already have this UID in the map
      const currentMap = uidToUsernameMapRef.current
      if (currentMap[uid] || currentMap[uidNum] || currentMap[uid.toString()]) {
        return
      }
      
      // Skip local user
      if (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid)) {
        return
      }
      
      // Look for username elements that already have parsed names (not JSON)
      const usernameSelectors = [
        '[class*="username"]',
        'span[style*="background"]',
        'p[style*="background"]',
        'div[style*="background"][style*="position"]',
      ]
      
      for (const selector of usernameSelectors) {
        const usernameEls = videoContainer.querySelectorAll(selector)
        for (const el of usernameEls) {
          const text = (el.textContent || el.innerText || '').trim()
          // If it's a valid name (not JSON, not "User {uid}", reasonable length)
          if (text && 
              !text.includes('{') && 
              !text.includes('"') &&
              !text.match(/^User \d+$/) &&
              text.length > 2 &&
              text.length < 50 &&
              text !== 'Loading...' &&
              text !== 'Unknown' &&
              text !== 'Screening Room') {
            // Found a parsed name! Store it as a plain string
            console.log('parseAndUpdateUsernames - Found already-parsed name in DOM for UID', uid, ':', text)
            setUidToUsernameMap((prev) => {
              if (!prev[uid] && !prev[uidNum] && !prev[uid.toString()]) {
                return {
                  ...prev,
                  [uid]: text, // Store as plain string
                  [uidNum]: text,
                  [uid.toString()]: text,
                }
              }
              return prev
            })
            return // Found name, move to next container
          }
        }
      }
    })
  }, [agoraRtmProps?.username, agoraRtcProps?.uid, enableLegacyDomEnhancements])

  // Apply custom styling to video cards after render
  useEffect(() => {
    if (!enableLegacyDomEnhancements) return
    if (!containerRef.current) return

    const applyCustomStyling = () => {
      const container = containerRef.current
      if (!container) return
      
      // First, run the aggressive JSON parser
      parseAndUpdateUsernames()

      // Find all video elements - use multiple selectors to catch all cases
      const videoSelectors = [
        '[class*="video"]',
        '[data-uid]',
        'div[style*="position"]',
        '[class*="Video"]',
      ]
      
      const allVideoElements = new Set()
      videoSelectors.forEach((selector) => {
        try {
          const elements = container.querySelectorAll(selector)
          elements.forEach((el) => allVideoElements.add(el))
        } catch (e) {
          // Selector failed, continue
        }
      })
      
      allVideoElements.forEach((videoEl) => {
        // Try to get uid from data-uid attribute first
        let uid = videoEl.getAttribute('data-uid')
        
        // If no data-uid, try to find it in child elements or parent
        if (!uid) {
          const uidElement = videoEl.querySelector('[data-uid]') || videoEl.closest('[data-uid]')
          if (uidElement) {
            uid = uidElement.getAttribute('data-uid')
          }
        }
        
        // If still no uid and this might be local video, check if it matches local uid
        if (!uid && agoraRtcProps?.uid !== undefined) {
          // Check if this element contains the local video by checking for specific patterns
          const isLocalVideo = videoEl.querySelector('video') && 
            !videoEl.closest('[data-uid]') && 
            videoEl === container.querySelector('[class*="local"], [class*="Local"]')
          if (isLocalVideo) {
            uid = agoraRtcProps.uid.toString()
          }
        }
        
        if (!uid) return

        // Get username - check map first, then fallback to RTM props for local user
        const uidNum = parseInt(uid)
        let username = uidToUsernameMap[uid] || uidToUsernameMap[uidNum] || uidToUsernameMap[uid.toString()]
        if (!username && parseInt(uid) === agoraRtcProps?.uid && agoraRtmProps?.username) {
          username = agoraRtmProps.username
          // Debug: log when we use RTM props for local user
          console.log('ModernChamberVideoInterface - Using RTM props for local user:', {
            uid,
            username,
            agoraRtcPropsUid: agoraRtcProps?.uid,
          })
        }
        
        // Set data-has-rtm attribute based on whether we have RTM data
        // Local user (uid 0) is always shown, remote users need RTM data
        if (uidNum === 0 || (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid))) {
          // Local user - always show
          videoEl.setAttribute('data-has-rtm', 'true')
        } else if (username) {
          // Remote user with RTM data - show it
          videoEl.setAttribute('data-has-rtm', 'true')
        } else {
          // Remote user without RTM data yet - keep RTC video visible while identity catches up
          videoEl.setAttribute('data-has-rtm', 'false')
        }
        
        // Debug: log username mapping
        if (username && parseInt(uid) === agoraRtcProps?.uid) {
          console.log('ModernChamberVideoInterface - Local user username:', {
            uid,
            username,
            isInMap: !!uidToUsernameMap[uid],
            fromRtmProps: !uidToUsernameMap[uid] && !!agoraRtmProps?.username,
          })
        }
        
        let isMentor = false
        
        if (username) {
          try {
            const parsed = JSON.parse(username)
            isMentor = parsed?.role === 'mentor'
          } catch (e) {
            // Not JSON, skip
          }
        }

        // Set role attribute for CSS targeting
        if (isMentor || parseInt(uid) === mentorUid) {
          videoEl.setAttribute('data-role', 'mentor')
        } else {
          videoEl.setAttribute('data-role', 'participant')
        }
        
        // Update data-has-rtm attribute (already set above, but ensure it's correct)
        // This ensures videos are shown/hidden based on RTM data availability
        const currentHasRtm = videoEl.getAttribute('data-has-rtm')
        const shouldHaveRtm = uidNum === 0 || (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid)) || !!username
        if (shouldHaveRtm && currentHasRtm !== 'true') {
          videoEl.setAttribute('data-has-rtm', 'true')
        } else if (!shouldHaveRtm && currentHasRtm !== 'false') {
          videoEl.setAttribute('data-has-rtm', 'false')
        }

        // Update username display - search within this video element and globally
        // Use comprehensive approach to find ALL text elements that might contain username
        const searchContainers = [videoEl, container]
        
        searchContainers.forEach((searchContainer) => {
          // First, try to find elements by common selectors
          const usernameSelectors = [
            `[data-uid="${uid}"] [class*="username"]`,
            `[data-uid="${uid}"] span`,
            `[data-uid="${uid}"] p`,
            `[data-uid="${uid}"] div[style*="background"]`,
            `[data-uid="${uid}"] div[style*="position"]`,
            `[class*="username"]`,
            `span`,
            `p`,
            `div[style*="background"]`,
          ]
          
          usernameSelectors.forEach((selector) => {
            try {
              const usernameEls = searchContainer.querySelectorAll(selector)
              usernameEls.forEach((usernameEl) => {
                if (!usernameEl) return
                
                const currentText = usernameEl.textContent || usernameEl.innerText || ''
                
                // Skip if this element is not related to this uid (check parent chain)
                const parentWithUid = usernameEl.closest('[data-uid]')
                if (parentWithUid && parentWithUid.getAttribute('data-uid') !== uid) {
                  return
                }
                
                // Check if current text looks like JSON (contains { and "name")
                const looksLikeJson = currentText.includes('{') && currentText.includes('"name"')
                
                // If we have a username, try to parse and update
                if (username) {
                  try {
                    const parsed = JSON.parse(username)
                    const role = parsed?.role
                    
                    // Hide auditor and panelist
                    if (role === 'auditor' || role === 'panelist') {
                      usernameEl.style.display = 'none'
                      return
                    }
                    
                    // Show name for any role (including chamber-room)
                    if (parsed?.name && parsed.name !== 'Unknown') {
                      // Always update if current text is JSON or different
                      if (looksLikeJson || currentText === username || currentText !== parsed.name) {
                        usernameEl.textContent = parsed.name
                        usernameEl.innerText = parsed.name
                        usernameEl.style.display = ''
                      }
                    } else if (parsed?.name === 'Unknown') {
                      usernameEl.style.display = 'none'
                    }
                  } catch (e) {
                    // If username is not JSON, but current text is JSON, try to extract from current text
                    if (looksLikeJson) {
                      try {
                        const parsedCurrent = JSON.parse(currentText)
                        if (parsedCurrent?.name && parsedCurrent.name !== 'Unknown') {
                          usernameEl.textContent = parsedCurrent.name
                          usernameEl.innerText = parsedCurrent.name
                          usernameEl.style.display = ''
                        }
                      } catch (e2) {
                        // Try regex extraction
                        const nameMatch = currentText.match(/"name"\s*:\s*"([^"]+)"/)
                        if (nameMatch && nameMatch[1]) {
                          usernameEl.textContent = nameMatch[1]
                          usernameEl.innerText = nameMatch[1]
                          usernameEl.style.display = ''
                        }
                      }
                    } else if (username && !username.includes('{') && !username.includes('"')) {
                      // Plain string username
                      if (currentText !== username) {
                        usernameEl.textContent = username
                        usernameEl.innerText = username
                        usernameEl.style.display = ''
                      }
                    }
                  }
                } else if (looksLikeJson) {
                  // No username in map, but current text is JSON - try to parse it
                  try {
                    const parsedCurrent = JSON.parse(currentText)
                    if (parsedCurrent?.name && parsedCurrent.name !== 'Unknown') {
                      usernameEl.textContent = parsedCurrent.name
                      usernameEl.innerText = parsedCurrent.name
                      usernameEl.style.display = ''
                    }
                  } catch (e2) {
                    // Try regex extraction
                    const nameMatch = currentText.match(/"name"\s*:\s*"([^"]+)"/)
                    if (nameMatch && nameMatch[1]) {
                      usernameEl.textContent = nameMatch[1]
                      usernameEl.innerText = nameMatch[1]
                      usernameEl.style.display = ''
                    }
                  }
                }
              })
            } catch (selectorError) {
              // Selector failed, continue
            }
          })
          
          // Also search for any text node that contains the JSON string directly
          // This is a fallback for cases where the JSON is in a text node
          if (username && username.includes('{')) {
            try {
              const parsed = JSON.parse(username)
              if (parsed?.name && parsed.name !== 'Unknown') {
                // Walk through all text nodes in the container
                const walker = document.createTreeWalker(
                  searchContainer,
                  NodeFilter.SHOW_TEXT,
                  null,
                  false
                )
                
                let node
                while ((node = walker.nextNode())) {
                  const text = node.textContent || ''
                  // Check if this text node contains the JSON
                  if (text.includes(username) || (text.includes('{') && text.includes('"name"') && text.includes(parsed.name))) {
                    // Find the parent element
                    let parent = node.parentElement
                    while (parent && parent !== searchContainer) {
                      // Check if this parent is related to our uid
                      const parentUid = parent.getAttribute('data-uid') || 
                        parent.closest('[data-uid]')?.getAttribute('data-uid')
                      if (parentUid === uid || (!parentUid && parseInt(uid) === agoraRtcProps?.uid)) {
                        // Update the text node
                        node.textContent = parsed.name
                        break
                      }
                      parent = parent.parentElement
                    }
                  }
                }
              }
            } catch (e) {
              // Failed to parse, skip
            }
          }
        })
      })
    }

    // Also update data-has-rtm attributes for all video elements
    const updateRtmAttributes = () => {
      if (!containerRef.current) return
      const allVideoElements = containerRef.current.querySelectorAll('[data-uid]')
      allVideoElements.forEach((videoEl) => {
        const uid = videoEl.getAttribute('data-uid')
        if (!uid) return
        
        const uidNum = parseInt(uid)
        const isLocalUser = uidNum === 0 || (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid))
        const hasRtmData = uidToUsernameMap[uid] || uidToUsernameMap[uidNum] || uidToUsernameMap[uid.toString()]
        
        if (isLocalUser) {
          videoEl.setAttribute('data-has-rtm', 'true')
        } else if (hasRtmData) {
          videoEl.setAttribute('data-has-rtm', 'true')
        } else {
          videoEl.setAttribute('data-has-rtm', 'false')
        }
      })
    }

    // Apply styling immediately and on interval
    applyCustomStyling()
    parseAndUpdateUsernames() // Run immediately
    styleStatusIndicators() // Style status indicators
    updateRtmAttributes() // Update RTM attributes immediately
    
    const interval = setInterval(() => {
      parseAndUpdateUsernames() // Run parser on interval too
      applyCustomStyling()
      styleStatusIndicators() // Style status indicators on interval
      updateRtmAttributes() // Update RTM attributes
    }, 100) // More frequent updates

    // Set up MutationObserver to watch for DOM changes (like mentor portal)
    const observer = new MutationObserver(() => {
      // When DOM changes, re-parse usernames immediately
      parseAndUpdateUsernames()
      // Also run styling after a small delay to ensure DOM is stable
      setTimeout(() => {
        applyCustomStyling()
        styleStatusIndicators()
        updateRtmAttributes() // Update RTM attributes when DOM changes
      }, 50)
    })

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true, // Watch for attribute changes too
        attributeFilter: ['data-uid', 'data-role'], // Only watch relevant attributes
      })
    }

    return () => {
      clearInterval(interval)
      observer.disconnect()
    }
  }, [uidToUsernameMap, agoraRtmProps?.username, agoraRtcProps?.uid, mentorUid, parseAndUpdateUsernames, styleStatusIndicators, enableLegacyDomEnhancements])
  
  // Also run parseAndUpdateUsernames when username map updates
  useEffect(() => {
    if (!enableLegacyDomEnhancements) return
    if (uidToUsernameMap && Object.keys(uidToUsernameMap).length > 0) {
      // Run immediately and after a delay
      parseAndUpdateUsernames()
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 50)
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 200)
    }
  }, [uidToUsernameMap, parseAndUpdateUsernames, enableLegacyDomEnhancements])

  // Periodically extract usernames from DOM and update map (fallback when rtm-user-joined doesn't fire)
  useEffect(() => {
    if (!enableLegacyDomEnhancements) return
    if (!containerRef.current) return

    const extractUsernamesFromDom = () => {
      try {
        // Find all video containers with data-uid
        const videoContainers = containerRef.current.querySelectorAll('[data-uid]')
        
        videoContainers.forEach((container) => {
          const uid = container.getAttribute('data-uid')
          if (!uid) return
          
          const uidNum = parseInt(uid)
          if (isNaN(uidNum)) return
          
          // Skip if we already have this UID in the map
          if (uidToUsernameMap[uid] || uidToUsernameMap[uidNum] || uidToUsernameMap[uid.toString()]) {
            return
          }
          
          // Skip local user
          if (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid)) {
            return
          }
          
          // Try to find username text in this container
          const usernameSelectors = [
            '[class*="username"]',
            'span[style*="background"]',
            'p[style*="background"]',
            'div[style*="background"][style*="position"]',
          ]
          
          for (const selector of usernameSelectors) {
            const usernameEls = container.querySelectorAll(selector)
            for (const el of usernameEls) {
              const text = (el.textContent || el.innerText || '').trim()
              // If it's a valid name (not JSON, not "User {uid}", reasonable length)
              if (text && 
                  !text.includes('{') && 
                  !text.includes('"') &&
                  !text.match(/^User \d+$/) &&
                  text.length > 2 &&
                  text.length < 50 &&
                  text !== 'Loading...' &&
                  text !== 'Unknown' &&
                  text !== 'Screening Room') {
                // Found a name! Store it (as plain string since it's not JSON)
                console.log('Extracted username from DOM for UID', uid, ':', text)
                setUidToUsernameMap((prev) => ({
                  ...prev,
                  [uid]: text,
                  [uidNum]: text,
                  [uid.toString()]: text,
                }))
                return // Found name, move to next container
              }
            }
          }
        })
      } catch (e) {
        console.error('Error extracting usernames from DOM:', e)
      }
    }

    // Run immediately
    extractUsernamesFromDom()
    
    // Also run periodically (every 2 seconds) to catch names that appear later
    const interval = setInterval(extractUsernamesFromDom, 2000)
    
    return () => clearInterval(interval)
  }, [containerRef, uidToUsernameMap, agoraRtcProps?.uid, enableLegacyDomEnhancements])
  
  // Run when RTM props update (especially for local user)
  useEffect(() => {
    if (!enableLegacyDomEnhancements) return
    if (agoraRtmProps?.username) {
      // Run immediately and after delays to catch all cases
      parseAndUpdateUsernames()
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 50)
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 200)
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 500)
    }
  }, [agoraRtmProps?.username, parseAndUpdateUsernames, enableLegacyDomEnhancements])

  // Memoize the placeholder wrapper to ensure it updates when uidToUsernameMap changes
  // This ensures remote users' names are properly displayed when RTM data arrives
  const customVideoPlaceholderWrapper = useMemo(() => {
    const StableChamberPlaceholderBridge = (props) => (
      <ChamberVideoPlaceholder
        {...props}
        uidToUsernameMap={uidToUsernameMapRef.current}
        chamberName={chamberName}
        localRtmUsername={agoraRtmProps?.username || ''}
      />
    )

    StableChamberPlaceholderBridge.displayName = 'StableChamberPlaceholderBridge'
    return StableChamberPlaceholderBridge
  }, [agoraRtmProps?.username, chamberName])

  const chamberScreenRtcMigrationDebugInfo = useMemo(
    () => ({
      enabled: true,
      provider: 'agora',
      renderer: 'shared-shell',
    }),
    [],
  )
  const resolvedRtcProps = useMemo(() => {
    const safeAgoraRtcProps =
      agoraRtcProps && typeof agoraRtcProps === 'object' ? agoraRtcProps : {}

    return {
      ...safeAgoraRtcProps,
      layout: AGORA_RTC_LAYOUTS.pin,
      CustomVideoPlaceholder: customVideoPlaceholderWrapper,
    }
  }, [
    agoraRtcProps,
    customVideoPlaceholderWrapper,
  ])
  const ChamberRtcVideoSurface = ChamberScreenAgoraVideoSurface
  const rtcSurfaceStyleProps = useMemo(
    () => ({
      UIKitContainer: {
        backgroundColor: 'transparent',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
      videoMode: {
        max: 'cover',
        min: 'cover',
      },
    }),
    [],
  )
  const rtcSurfaceLoadingFallback = (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        background:
          'linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 41, 59, 0.92) 100%)',
        color: '#f8fafc',
        zIndex: 2,
      }}
    >
      <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
        Connecting chamber video...
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          color: 'rgba(241, 245, 249, 0.78)',
          textAlign: 'center',
          maxWidth: '420px',
          px: 3,
        }}
      >
        Camera, microphone, and screen controls are being restored.
      </Typography>
    </Box>
  )

  useEffect(() => {
    const debugPayload = {
      componentPath: 'modern-chamber-video-interface',
      clinicBrandId: clinicBrandId || null,
      flowType: flowType || null,
      chamberName: chamberName || null,
      joinedChannelName: agoraRtcProps?.channel || null,
      debugVersion: 'chamber-screen-shared-renderer-v1',
      rawFeatureFlags: sessionRoutingDecision?.featureFlags || null,
      ...chamberScreenRtcMigrationDebugInfo,
    }

    if (typeof window !== 'undefined') {
      window.__AUMHUM_CHAMBER_SCREEN_RTC_DEBUG__ = debugPayload
    }

    console.info('[ModernChamberVideoInterface] RTC surface selection', debugPayload)
  }, [
    agoraRtcProps?.channel,
    chamberName,
    chamberScreenRtcMigrationDebugInfo,
    clinicBrandId,
    flowType,
    sessionRoutingDecision,
  ])

  return (
    <VideoContainer
      ref={containerRef}
      className={[
        'chamber-video-container',
        'modern-kiosk-stage',
        showControls ? '' : 'controls-hidden',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ModernKioskVideoCardStyles />

      {/* Hero Video Wrapper - Mentor gets prominent display */}
      <HeroVideoWrapper>
        <ChamberRtcVideoSurface
          shell={chamberShell}
          rtcProps={resolvedRtcProps}
          callbacks={enhancedCallbacks}
          styleProps={rtcSurfaceStyleProps}
          rtmProps={agoraRtmProps}
          fallback={rtcSurfaceLoadingFallback}
          handleLeaveRoom={agoraCallbacks?.EndCall}
        />
      </HeroVideoWrapper>

      {chamberShell.surfacePolicy?.allowManualFocus !== false ? (
        <Box
          className="focus-selection-button"
          sx={{
            position: 'absolute',
            bottom: 24,
            right: { xs: 92, md: 104 },
            zIndex: 140,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          <Tooltip
            title={
              chamberShell.isManualFocusActive
                ? 'Change focused participant'
                : 'Select video to focus'
            }
            arrow
          >
            <IconButton
              onClick={(e) => setFocusMenuAnchor(e.currentTarget)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(148, 163, 184, 0.28)',
                color: '#0f172a',
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                boxShadow: '0 18px 42px rgba(2, 6, 23, 0.24)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  borderColor: 'rgba(96, 165, 250, 0.46)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CenterFocusStrongIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
      
      {chamberShell.surfacePolicy?.allowManualFocus !== false ? (
        <>
      {/* Focus Selection Menu */}
      <Menu
        anchorEl={focusMenuAnchor}
        open={focusMenuOpen}
        onClose={() => setFocusMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxHeight: 400,
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(103, 121, 227, 0.1)',
            overflow: 'auto',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        {chamberShell.isManualFocusActive ? (
          <MenuItem
            onClick={() => {
              setFocusedUid(null)
              chamberShell.clearManualFocus()
              setFocusMenuAnchor(null)
            }}
            sx={{
              py: 1.25,
              px: 2,
              borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
              color: '#0f172a',
              fontWeight: 600,
            }}
          >
            Clear Manual Focus
          </MenuItem>
        ) : null}
        {focusMenuParticipants.map((participant) => {
          const isSelected =
            activeSpotlightUid !== null &&
            activeSpotlightUid !== undefined &&
            String(participant.uid) === String(activeSpotlightUid)
          const participantProfilePicUrl = getProfilePictureUrl(participant.profilePicId)

          return (
            <MenuItem
              key={participant.uid}
              selected={isSelected}
              onClick={() => {
                setFocusedUid(participant.focusUid)
                chamberShell.setPreferredDominantParticipantId(participant.participantId)
                setFocusMenuAnchor(null)
              }}
              sx={{
                py: 1.5,
                px: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(96, 165, 250, 0.14)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.18)',
                },
                '&:hover': {
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Avatar
                  src={participantProfilePicUrl || undefined}
                  sx={{
                    width: 34,
                    height: 34,
                    background: participantProfilePicUrl
                      ? 'transparent'
                      : 'linear-gradient(135deg, #6779E3 0%, #7450AA 100%)',
                    fontSize: '14px',
                    fontWeight: 600,
                    border: '2px solid rgba(103, 121, 227, 0.16)',
                    objectFit: 'cover',
                  }}
                  imgProps={{
                    onError: (event) => {
                      event.target.style.display = 'none'
                      event.target.src = ''
                    },
                    crossOrigin: 'anonymous',
                    loading: 'eager',
                  }}
                >
                  {!participantProfilePicUrl && getInitials(participant.displayName)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={participant.displayName}
                secondary={participant.roleLabel}
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#334155',
                }}
                secondaryTypographyProps={{
                  fontSize: '11px',
                  color: 'rgba(71, 85, 105, 0.82)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              />
            </MenuItem>
          )
        })}
        {focusMenuParticipants.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.5)' }}>
              No participants available
            </Typography>
          </MenuItem>
        )}
      </Menu>
        </>
      ) : null}

    </VideoContainer>
  )
}
